import React, { useMemo, useState, useEffect } from 'react';
import {
  Input,
  Tag,
  Radio,
  Button,
  Progress,
  Collapse,
  Empty,
  Segmented,
  DatePicker,
  Table,
} from 'antd';
import {
  SearchOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  TableOutlined,
  ArrowRightOutlined,
  SyncOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { toMonthDayjs } from '../utils/monthDayjs';
import './PayrollInspection.css';

const fmtInr = (n) => {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const fmtVal = (n, field, raw) => {
  if (raw != null && String(raw).trim() !== '' && (n == null || n === '')) return String(raw);
  if (n == null || n === '') return '—';
  if (['netSalary', 'otPay', 'outstandingAdvance', 'advanceDeduction', 'grossSalary'].includes(field)) return fmtInr(n);
  return String(n);
};

const DAY_FILTER_OPTIONS = [
  { label: 'All days', value: 'all' },
  { label: 'OT', value: 'ot' },
  { label: 'Late', value: 'late' },
  { label: 'No punch (system absent)', value: 'noPunch' },
  { label: 'Marked absent', value: 'markedAbsent' },
  { label: 'Weekly off / holiday', value: 'off' },
  { label: 'Leave', value: 'leave' },
];

function DayTimelinePanel({ timeline }) {
  const [filter, setFilter] = useState('all');
  const tl = timeline;
  if (!tl?.days?.length) {
    return <Empty description="Re-run analysis to load day-by-day attendance (or check compare month)." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }
  const sum = tl.issueSummary || {};
  const filtered = tl.days.filter((row) => {
    if (filter === 'all') return true;
    if (filter === 'ot') return row.otMinutes > 0;
    if (filter === 'late') return row.lateMinutes > 0;
    if (filter === 'noPunch') return row.source === 'system';
    if (filter === 'markedAbsent') return row.status === 'A' && row.source === 'attendance';
    if (filter === 'off') return row.status === 'WO' || row.status === 'H';
    if (filter === 'leave') return row.status === 'L';
    return true;
  });
  const columns = [
    { title: 'Date', dataIndex: 'date', width: 108 },
    { title: 'Status', dataIndex: 'label', render: (_, r) => <Tag>{r.label}</Tag> },
    { title: 'OT (min)', dataIndex: 'otMinutes', width: 88, render: (v) => (v > 0 ? v : '—') },
    { title: 'Late (min)', dataIndex: 'lateMinutes', width: 92, render: (v) => (v > 0 ? v : '—') },
    {
      title: 'Punch in',
      dataIndex: 'punchedIn',
      width: 100,
      render: (v) => (v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      width: 100,
      render: (s) => ({ attendance: 'Punch', roster: 'Roster', system: 'System', leave: 'Leave', none: '—' }[s] || s),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      ellipsis: true,
      render: (n, r) => n || (r.autoPunchout ? 'Auto punch-out' : ''),
    },
  ];
  return (
    <div>
      <p style={{ fontSize: 12, color: '#727786', marginBottom: 8 }}>{tl.hint}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <Tag color="blue">OT days: {(sum.ot || []).length}</Tag>
        <Tag color="orange">Late days: {(sum.late || []).length}</Tag>
        <Tag color="red">No punch: {(sum.noPunchAbsent || []).length}</Tag>
        <Tag>Marked absent: {(sum.markedAbsent || []).length}</Tag>
        <Tag color="default">Weekly off: {(sum.weeklyOff || []).length}</Tag>
        <Tag color="default">Holiday: {(sum.holiday || []).length}</Tag>
      </div>
      <Segmented options={DAY_FILTER_OPTIONS} value={filter} onChange={setFilter} size="small" style={{ marginBottom: 10 }} />
      <Table
        size="small"
        rowKey="date"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 15, showSizeChanger: false }}
        scroll={{ x: 720 }}
      />
    </div>
  );
}

const pickExcel = (row, field) => {
  const m = row?.excelMetrics?.[field];
  if (m?.raw || m?.value != null) return m;
  const json = row?.excelRowJson || {};
  const aliases = {
    netSalary: ['net', 'payable', 'salary'],
    outstandingAdvance: ['outstanding', 'balance', 'remaining'],
    advanceDeduction: ['advance deduction', 'adv deduction', 'deduction', 'emi', 'recovery'],
    present: ['present', 'payable', 'working'],
    absent: ['absent', 'lop'],
    otHours: ['ot', 'overtime'],
    grossSalary: ['gross'],
  }[field] || [];
  for (const [k, v] of Object.entries(json)) {
    const nk = String(k).toLowerCase();
    if (aliases.some((a) => nk.includes(a)) && String(v).trim() !== '') {
      return { raw: String(v), value: Number(String(v).replace(/[,₹]/g, '')) || null, column: k };
    }
  }
  return null;
};

const initials = (name) => {
  const p = String(name || '?').trim().split(/\s+/);
  return (p[0]?.[0] || '') + (p[1]?.[0] || '');
};

function rowKey(row) {
  return row.match?.userId ? `u-${row.match.userId}` : `r-${row.rowNumber}`;
}

export default function PayrollInspectionCompareView({
  report,
  monthKey,
  fileName,
  resolutions,
  setResolutions,
  loading,
  onApplyStaff,
  onApplyAll,
  onBack,
  onMonthChange,
}) {
  const [filter, setFilter] = useState('diffs');
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);

  const rows = report?.rows || [];
  const summary = report?.summary || {};

  const buckets = useMemo(() => {
    const withDiffs = rows.filter((r) => r.match?.userId && r.differences.length > 0);
    const clean = rows.filter((r) => r.match?.userId && r.differences.length === 0);
    const unmatched = rows.filter((r) => !r.match?.userId);
    return { withDiffs, clean, unmatched, all: rows };
  }, [rows]);

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchSearch = (r) => {
      if (!q) return true;
      const name = (r.match?.name || '').toLowerCase();
      const id = String(r.match?.staffId || '').toLowerCase();
      const err = (r.match?.error || '').toLowerCase();
      return name.includes(q) || id.includes(q) || err.includes(q);
    };
    if (filter === 'diffs') return buckets.withDiffs.filter(matchSearch);
    if (filter === 'matched') return buckets.clean.filter(matchSearch);
    if (filter === 'unmatched') return buckets.unmatched.filter(matchSearch);
    return rows.filter(matchSearch);
  }, [filter, search, buckets, rows]);

  useEffect(() => {
    if (!selectedKey && filteredList.length) {
      setSelectedKey(rowKey(filteredList[0]));
    }
  }, [filteredList, selectedKey]);

  const selectedRow = useMemo(
    () => rows.find((r) => rowKey(r) === selectedKey) || filteredList[0],
    [rows, selectedKey, filteredList]
  );

  const pendingForStaff = (row) =>
    (row?.differences || []).filter((d) => !resolutions[d.id] || resolutions[d.id] === 'excel').length;

  const resolvedCount = (row) => {
    const diffs = row?.differences || [];
    return diffs.filter((d) => resolutions[d.id] === 'software' || resolutions[d.id] === 'excel').length;
  };

  const monthDayjs = toMonthDayjs(monthKey);
  const monthLabel = monthDayjs ? monthDayjs.format('MMMM YYYY') : '—';

  const excelNet = pickExcel(selectedRow, 'netSalary');
  const excelAdvDed = pickExcel(selectedRow, 'advanceDeduction');
  const excelAdvBal = pickExcel(selectedRow, 'outstandingAdvance');
  const excelPresent = pickExcel(selectedRow, 'present');
  const swNet = selectedRow?.software?.netSalary;
  const swAdvDed = selectedRow?.software?.advanceDeductionThisMonth;
  const swAdvBal = selectedRow?.software?.outstandingAdvanceTotal;
  const swDataSource = selectedRow?.software?.dataSource;
  const noPayroll = selectedRow?.software && swDataSource === 'none';
  const liveCalc = swDataSource === 'live_calculation';

  return (
    <div className="pi-page">
      <div className="pi-step-cards">
        <div className="pi-step-card done">
          <div className="pi-step-num"><CheckCircleOutlined /></div>
          <div>
            <div><strong>Step 1: Upload Excel</strong> <Tag color="success">Done</Tag></div>
            <div style={{ fontSize: 12, color: '#727786' }}>{fileName || 'Uploaded file'}</div>
          </div>
        </div>
        <div className="pi-step-card done">
          <div className="pi-step-num"><CheckCircleOutlined /></div>
          <div>
            <div><strong>Step 2: Worksheet</strong> <Tag color="success">Mapped</Tag></div>
            <div style={{ fontSize: 12, color: '#727786' }}>
              Sheet: {report?.sheetName} | {monthLabel}
            </div>
          </div>
        </div>
        <div className="pi-step-card active">
          <div className="pi-step-num">3</div>
          <div>
            <div><strong>Step 3: Compare &amp; Fix</strong> <Tag color="processing">In progress</Tag></div>
            <div style={{ fontSize: 12, color: '#004398' }}>Side-by-side reconciliation</div>
          </div>
        </div>
      </div>

      <div className="pi-stats-bar">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span className="pi-stat-pill">Total rows: <strong>{summary.totalRows ?? 0}</strong></span>
            <span className="pi-stat-pill" style={{ background: 'rgba(48,134,0,0.1)', color: '#256a00' }}>
              Staff matched: <strong>{summary.matched ?? 0}</strong>
            </span>
            {(summary.unmatched ?? 0) > 0 && (
              <span className="pi-stat-pill" style={{ background: 'var(--pi-err-bg)', color: 'var(--pi-err)' }}>
                Unmatched: <strong>{summary.unmatched}</strong>
              </span>
            )}
            <span className="pi-stat-pill" style={{ background: 'var(--pi-warn-bg)' }}>
              With differences: <strong>{summary.staffWithDifferences ?? summary.withDifferences ?? 0}</strong>
            </span>
            <span className="pi-stat-pill">Clean: <strong>{summary.cleanMatches ?? 0}</strong></span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                background: '#d9e2ff',
                borderRadius: 8,
                border: '1px solid #afc6ff',
              }}
            >
              <CalendarOutlined style={{ color: '#0057c2' }} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Compare month:</span>
              <DatePicker
                picker="month"
                value={monthDayjs}
                onChange={(d) => {
                  const next = toMonthDayjs(d);
                  if (next) onMonthChange?.(next);
                }}
                format="MMMM YYYY"
                allowClear={false}
                disabled={loading}
                size="small"
              />
              <Button
                type="link"
                size="small"
                icon={<SyncOutlined />}
                loading={loading}
                onClick={() => monthDayjs && onMonthChange?.(monthDayjs)}
              >
                Reload
              </Button>
            </div>
            <Button type="default" size="small" onClick={onBack}>Change worksheet</Button>
          </div>
        </div>

        <div className="pi-filters">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1, alignItems: 'center' }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search staff name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 320 }}
              allowClear
            />
            <Segmented
              value={filter}
              onChange={setFilter}
              options={[
                { label: `All (${rows.length})`, value: 'all' },
                { label: `With diffs (${buckets.withDiffs.length})`, value: 'diffs' },
                { label: `Matched (${buckets.clean.length})`, value: 'matched' },
                { label: `Unmatched (${buckets.unmatched.length})`, value: 'unmatched' },
              ]}
            />
          </div>
          <Tag icon={<CalendarOutlined />} color="blue">
            DB month: {monthLabel}
          </Tag>
        </div>
      </div>

      <div className="pi-master-detail">
        <div className="pi-staff-list">
          {filter !== 'unmatched' && filter !== 'matched' && buckets.withDiffs.length > 0 && (
            <>
              <div className="pi-section-label">
                <span><WarningOutlined style={{ color: '#904d00' }} /> Requires resolution ({buckets.withDiffs.length})</span>
                <Tag color="orange">Priority</Tag>
              </div>
              {(filter === 'diffs' ? filteredList : buckets.withDiffs.filter((r) => {
                const q = search.trim().toLowerCase();
                if (!q) return true;
                return (r.match?.name || '').toLowerCase().includes(q)
                  || String(r.match?.staffId || '').toLowerCase().includes(q);
              })).map((r) => (
                <StaffCard
                  key={rowKey(r)}
                  row={r}
                  selected={rowKey(r) === rowKey(selectedRow)}
                  onClick={() => setSelectedKey(rowKey(r))}
                />
              ))}
            </>
          )}

          {filter === 'matched' && filteredList.map((r) => (
            <StaffCard
              key={rowKey(r)}
              row={r}
              selected={rowKey(r) === rowKey(selectedRow)}
              onClick={() => setSelectedKey(rowKey(r))}
            />
          ))}

          {filter === 'all' && filteredList.filter((r) => r.match?.userId && !r.differences.length).length > 0 && (
            <Collapse
              ghost
              style={{ marginTop: 8 }}
              items={[{
                key: 'clean',
                label: <span><CheckCircleOutlined style={{ color: '#256a00' }} /> Clean matches ({buckets.clean.length})</span>,
                children: buckets.clean.map((r) => (
                  <StaffCard
                    key={rowKey(r)}
                    row={r}
                    selected={rowKey(r) === rowKey(selectedRow)}
                    onClick={() => setSelectedKey(rowKey(r))}
                  />
                )),
              }]}
            />
          )}

          {buckets.unmatched.length > 0 && (
            <>
              <div className="pi-section-label">
                <span><CloseCircleOutlined style={{ color: 'var(--pi-err)' }} /> Could not match ({buckets.unmatched.length})</span>
              </div>
              {buckets.unmatched.map((r) => (
                <div key={rowKey(r)} className="pi-staff-card" style={{ background: 'var(--pi-err-bg)', cursor: 'default' }}>
                  <div style={{ fontWeight: 600 }}>Row #{r.rowNumber}</div>
                  <div style={{ fontSize: 12, color: 'var(--pi-err)' }}>{r.match?.error}</div>
                </div>
              ))}
            </>
          )}

        </div>

        <div className="pi-compare-panel">
          {!selectedRow ? (
            <Empty description="Select a staff member from the list" />
          ) : !selectedRow.match?.userId ? (
            <Empty description="Map this row to an employee (coming soon)" />
          ) : (
            <>
              <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 12, background: '#0057c2', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18,
                    }}>
                      {initials(selectedRow.match.name)}
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{selectedRow.match.name}</div>
                      <div style={{ fontSize: 13, color: '#727786' }}>
                        {selectedRow.match.staffId || '—'} • Row #{selectedRow.rowNumber} • {monthLabel}
                      </div>
                    </div>
                  </div>
                  <div style={{ minWidth: 160 }}>
                    <div style={{ fontSize: 12, color: '#727786' }}>Resolution progress</div>
                    <Progress
                      percent={selectedRow.differences.length
                        ? Math.round((resolvedCount(selectedRow) / selectedRow.differences.length) * 100)
                        : 100}
                      size="small"
                    />
                  </div>
                </div>

                <div className="pi-summary-duo" style={{ marginTop: 16 }}>
                  <div className="pi-excel-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span><TableOutlined /> <strong>Your Excel</strong></span>
                      <span style={{ fontSize: 12, color: '#727786' }}>Row #{selectedRow.rowNumber}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                      <div>
                        <span style={{ color: '#727786', fontSize: 11 }}>Net salary</span><br />
                        <strong>{fmtVal(excelNet?.value, 'netSalary', excelNet?.raw)}</strong>
                        {excelNet?.column && <div style={{ fontSize: 10, color: '#727786' }}>Col: {excelNet.column}</div>}
                      </div>
                      <div>
                        <span style={{ color: '#727786', fontSize: 11 }}>Present / payable days</span><br />
                        <strong>{fmtVal(excelPresent?.value, 'present', excelPresent?.raw)}</strong>
                      </div>
                      <div>
                        <span style={{ color: '#727786', fontSize: 11 }}>Advance deduction (month)</span><br />
                        <strong>{fmtVal(excelAdvDed?.value, 'advanceDeduction', excelAdvDed?.raw)}</strong>
                      </div>
                      <div>
                        <span style={{ color: '#727786', fontSize: 11 }}>Outstanding advance</span><br />
                        <strong>{fmtVal(excelAdvBal?.value, 'outstandingAdvance', excelAdvBal?.raw)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="pi-db-card">
                    <div style={{ marginBottom: 8 }}><DatabaseOutlined /> <strong>Thinktech (software)</strong></div>
                    {noPayroll && (
                      <div style={{ fontSize: 12, color: '#904d00', marginBottom: 8, padding: 8, background: '#fff', borderRadius: 8 }}>
                        No system data found for {monthLabel}. Confirm the compare month matches Reports (e.g. October 2026).
                      </div>
                    )}
                    {liveCalc && (
                      <div style={{ fontSize: 12, color: '#004398', marginBottom: 8, padding: 8, background: '#fff', borderRadius: 8 }}>
                        Payroll is not locked — attendance and net salary are from live calculation (same logic as Org Reports).
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                      <div><span style={{ color: '#004398', fontSize: 11 }}>Net calculated</span><br /><strong style={{ color: '#0057c2' }}>{fmtInr(swNet)}</strong></div>
                      <div><span style={{ color: '#004398', fontSize: 11 }}>Advance deduction (month)</span><br /><strong style={{ color: '#0057c2' }}>{fmtInr(swAdvDed)}</strong></div>
                      <div><span style={{ color: '#004398', fontSize: 11 }}>Outstanding advance</span><br /><strong style={{ color: '#0057c2' }}>{fmtInr(swAdvBal)}</strong></div>
                      <div><span style={{ color: '#004398', fontSize: 11 }}>Present / payable (system)</span><br /><strong>{selectedRow.software?.attendance?.present ?? '—'}</strong></div>
                      <div><span style={{ color: '#004398', fontSize: 11 }}>Absent (system)</span><br /><strong>{selectedRow.software?.attendance?.absent ?? '—'}</strong></div>
                      <div><span style={{ color: '#004398', fontSize: 11 }}>OT minutes (system)</span><br /><strong>{selectedRow.software?.attendance?.otMinutes ?? '—'}</strong></div>
                    </div>
                  </div>
                </div>

                {selectedRow.excelRowJson && (
                  <details style={{ marginTop: 12, fontSize: 12 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>All Excel columns for this row</summary>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 6, marginTop: 8, maxHeight: 160, overflow: 'auto' }}>
                      {Object.entries(selectedRow.excelRowJson)
                        .filter(([, v]) => String(v).trim() !== '')
                        .map(([k, v]) => (
                          <div key={k} style={{ background: '#fff', padding: '4px 8px', borderRadius: 6, border: '1px solid #e4e2e2' }}>
                            <div style={{ color: '#727786', fontSize: 10 }}>{k}</div>
                            <div style={{ fontWeight: 600 }}>{v}</div>
                          </div>
                        ))}
                    </div>
                  </details>
                )}

                {selectedRow.differences.length > 0 && (
                  <div style={{
                    marginTop: 12, padding: 10, borderRadius: 8, background: 'rgba(255,220,195,0.5)',
                    fontSize: 13,
                  }}>
                    <SyncOutlined style={{ color: '#904d00' }} />
                    {' '}
                    <strong>{selectedRow.differences.length} field(s)</strong> differ — choose per field: update software or fix Excel.
                  </div>
                )}
              </div>

              <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ padding: '12px 16px', background: '#f5f3f3', fontWeight: 600 }}>
                  Itemized compare ({selectedRow.differences.length} inspected fields)
                </div>

                {selectedRow.differences.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <CheckCircleOutlined style={{ fontSize: 32, color: '#256a00' }} />
                    <p>All compared fields match for this employee.</p>
                  </div>
                ) : (
                  selectedRow.differences.map((d, idx) => {
                    const isMatch = false;
                    return (
                      <div key={d.id} className={`pi-field-block ${isMatch ? 'matched' : 'diff'}`}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8 }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{idx + 1}. {d.label}</div>
                            <div style={{ fontSize: 12, color: '#727786', marginTop: 2 }}>{d.impact}</div>
                          </div>
                          <div className="pi-value-pair">
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 11, color: '#727786' }}>Excel{d.excelColumn ? ` (${d.excelColumn})` : ''}</div>
                              <strong>{fmtVal(d.excelValue, d.field, d.excelRaw)}</strong>
                            </div>
                            <ArrowRightOutlined style={{ color: '#727786' }} />
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 11, color: '#0057c2' }}>Software</div>
                              <strong style={{ color: '#ba1a1a' }}>{fmtVal(d.softwareValue, d.field)}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="pi-decision-box">
                          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Reconciliation decision</div>
                          <Radio.Group
                            value={resolutions[d.id] || 'excel'}
                            onChange={(e) => setResolutions((prev) => ({ ...prev, [d.id]: e.target.value }))}
                            style={{ width: '100%' }}
                          >
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <Radio value="software" style={{ alignItems: 'flex-start', padding: 8, background: '#fff', borderRadius: 8, width: '100%' }}>
                                <div>
                                  <div style={{ fontWeight: 500 }}>Update Thinktech DB</div>
                                  <div style={{ fontSize: 12, color: '#727786' }}>Software is wrong — update Thinktech to match Excel</div>
                                </div>
                              </Radio>
                              <Radio value="excel" style={{ alignItems: 'flex-start', padding: 8, background: 'rgba(217,226,255,0.4)', borderRadius: 8, width: '100%' }}>
                                <div>
                                  <div style={{ fontWeight: 600, color: '#0057c2' }}>Fix Excel (recommended)</div>
                                  <div style={{ fontSize: 12, color: '#727786' }}>Excel is wrong — fix the sheet; keep software as-is</div>
                                </div>
                              </Radio>
                            </div>
                          </Radio.Group>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <Collapse
                defaultActiveKey={['days']}
                items={[
                  {
                    key: 'days',
                    label: 'Day-by-day breakdown (OT, absent, late)',
                    children: (
                      <DayTimelinePanel timeline={selectedRow.software?.dayTimeline} />
                    ),
                  },
                  {
                    key: 'ctx',
                    label: 'System context (automation, advances, leaves)',
                    children: (
                      <pre style={{ maxHeight: 240, overflow: 'auto', fontSize: 11, margin: 0 }}>
                        {JSON.stringify(selectedRow.software, null, 2)}
                      </pre>
                    ),
                  },
                  {
                    key: 'json',
                    label: `Excel row JSON (row #${selectedRow.rowNumber})`,
                    children: (
                      <pre style={{ maxHeight: 240, overflow: 'auto', fontSize: 11, margin: 0 }}>
                        {JSON.stringify(selectedRow.excelRowJson, null, 2)}
                      </pre>
                    ),
                  },
                ]}
              />

              <div className="pi-sticky-footer">
                <div>
                  <strong>{selectedRow.match.name}</strong>
                  <span style={{ color: '#727786', marginLeft: 8 }}>
                    • {pendingForStaff(selectedRow)} pending decision(s)
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button onClick={() => onApplyStaff(selectedRow)} loading={loading} type="default">
                    Apply choices for this staff
                  </Button>
                  <Button type="primary" onClick={onApplyAll} loading={loading}>
                    Apply all ({buckets.withDiffs.length} with diffs)
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StaffCard({ row, selected, onClick }) {
  const preview = row.differences.slice(0, 2).map((d) => d.label).join(' • ');
  return (
    <div className={`pi-staff-card ${selected ? 'selected' : ''}`} onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600 }}>{row.match?.name}</div>
          <div style={{ fontSize: 12, color: '#727786' }}>{row.match?.staffId || '—'}</div>
        </div>
        <Tag color={row.differences.length ? 'error' : 'success'}>
          {row.differences.length} diff{row.differences.length !== 1 ? 's' : ''}
        </Tag>
      </div>
      {preview && (
        <div style={{ fontSize: 12, color: '#727786', marginTop: 6 }}>{preview}</div>
      )}
    </div>
  );
}
